import type { BoardSummary, Schedule, Task, TaskStatus } from "../shared/types";

/**
 * The last summary each board showed, kept for the life of the page so a screen can draw before its
 * own request returns: switching To Do and Done paints the list it showed last time and refreshes
 * behind it, and a task screen opens with the task the board already had instead of a skeleton.
 * Nothing here is authoritative; every screen still fetches and replaces it.
 *
 * Bounded on purpose. Only the server's first page is ever remembered (the Done tab's "Show older"
 * rows are appended to the screen, never here), and a page longer than MAX_REMEMBERED_TASKS is not
 * remembered at all rather than remembered in part, so a screen painted from memory is always the
 * whole first page: the fetch that follows then changes nothing visible unless the data moved.
 * Three entries at most (two boards, one schedule list), each a few KB of ids, titles and dates;
 * no photo bytes live here (thumbnails go through the browser's image cache, lazily, as before).
 */
export const MAX_REMEMBERED_TASKS = 60;
export const MAX_REMEMBERED_SCHEDULES = 60;

const summaries = new Map<TaskStatus, BoardSummary>();
let schedules: Schedule[] | null = null;

export function rememberBoard(status: TaskStatus, summary: BoardSummary): void {
  if (summary.tasks.length > MAX_REMEMBERED_TASKS) summaries.delete(status);
  else summaries.set(status, summary);
}

export function recallBoard(status: TaskStatus): BoardSummary | null {
  return summaries.get(status) ?? null;
}

/** The task as a board last saw it, from either list, or null when no board has shown it. */
export function recallTask(id: string): Task | null {
  for (const summary of summaries.values()) {
    const task = summary.tasks.find((t) => t.id === id);
    if (task) return task;
  }
  return null;
}

/** The schedule list as last shown, for the same reason: the Schedules tab paints before it fetches. */
export function rememberSchedules(list: Schedule[]): void {
  schedules = list.length > MAX_REMEMBERED_SCHEDULES ? null : list;
}

export function recallSchedules(): Schedule[] | null {
  return schedules;
}

/**
 * How far down each board was scrolled when it was left, so Back from a task lands where the peon
 * was reading instead of at the top. Taken once: a board opened fresh (a tab press) starts at the top.
 */
const scrolls = new Map<TaskStatus, number>();

/** The screens that sit on a board: coming back from one of them is a return, not a fresh open. */
const BOARD_CHILDREN = new Set(["task", "newTask", "hall", "rewards", "usage", "house", "peon"]);

export function rememberBoardScroll(status: TaskStatus, top: number): void {
  if (top > 0) scrolls.set(status, top);
  else scrolls.delete(status);
}

/**
 * The scroll to restore on a board that just mounted, or null to start at the top: only when the
 * screen it came from (`from`, a route name) sits on a board. Forgotten either way.
 */
export function takeBoardScroll(status: TaskStatus, from: string | null): number | null {
  const top = scrolls.get(status) ?? null;
  scrolls.delete(status);
  return top !== null && from !== null && BOARD_CHILDREN.has(from) ? top : null;
}

/** After a household switch nothing remembered applies. */
export function forgetBoards(): void {
  summaries.clear();
  schedules = null;
  scrolls.clear();
}

/** How much is remembered right now, for the workshop's device log and tests. */
export function rememberedSize(): { boards: number; tasks: number; schedules: number } {
  let tasks = 0;
  for (const summary of summaries.values()) tasks += summary.tasks.length;
  return { boards: summaries.size, tasks, schedules: schedules?.length ?? 0 };
}
